import { createLogic } from 'redux-logic';
import * as Action from './action';
import * as Types from './types';
import _ from 'lodash';
import debugLog, { LogLevel } from '../utils/debugLog';

const updateSegmentsLogic = createLogic({
  type: Types.GET_BUS_ETA,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action, httpClient, getState }: any, dispatch, done) {
    const { routes } = action.payload;

    let allRouteLocation: any[] = [];

    try {
      const routesRes = await httpClient.get(
        `${process.env.REACT_APP_SERVER_URL}/api/routesByIds?Ids=${_.map(
          routes,
          (route) => route.id
        )}`
      );

      routesRes.data.forEach((route: any) => {
        if (route.status === 'active') {
          allRouteLocation = [...allRouteLocation, ...route.routeLocations];
        }
      });

      dispatch(Action.getAllRouteLocation(allRouteLocation));

      const uniqueLocations: any[] = [];
      allRouteLocation.forEach((location) => {
        const target = _.find(uniqueLocations, {
          locationId: location.locationId,
        });
        if (target) {
          target.routeIds = _.uniq([...target.routeIds, location.routeId]);
        } else {
          uniqueLocations.push({ ...location, routeIds: [location.routeId] });
        }
      });

      // add new origin of routeLocations from API (routesRes.data) to uniqueLocations
      const uniqueLocationsWithRouteLocations = uniqueLocations.map((uniqueLocation) => {
        // Find the route in routesRes.data that matches this uniqueLocation’s routeId
        const matchingRoute = routesRes.data.find(
          (route: any) => route.id === uniqueLocation.routeId
        );

        // If found, filter routeLocations to be unique by locationId
        const routeLocations = matchingRoute
          ? _.sortBy(_.uniqBy(matchingRoute.routeLocations, 'locationId'), 'order')
          : [];

        return {
          ...uniqueLocation,
          routeLocations,
        };
      });

      dispatch(Action.updateLocationList(uniqueLocationsWithRouteLocations));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `updateSegmentsLogic()`. ', {
        error: error,
      });
      throw error;
    }

    done();
  },
});

const getAllEtaLogic = createLogic({
  type: Types.GET_ALL_ETA,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action, httpClient }: any, dispatch, done) {
    const { data } = action.payload;

    try {
      // Fetch data
      var allEta: any = [];
      data.forEach(async (route: any) => {
        const response = await httpClient.get(
          `${process.env.REACT_APP_SERVER_URL}/api/directions/${route.id}`
        );

        if (response.status === 200 && response.data) {
          allEta.push(response.data);
        } else {
          console.warn(`Failed to fetch data for route ${route.id}`);
        }
      });

      dispatch(Action.getAllEtaSuccess(allEta));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `getAllEtaLogic()`. ', {
        error: error,
      });
      console.error('Error fetching data:', error);
    }
    done();
  },
});

const getRouteTagsLogic = createLogic({
  type: Types.GET_ROUTE_TAGS,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ httpClient }: any, dispatch, done) {
    try {
      const tenantId = process.env.REACT_APP_TENANT_ID;

      const [routeTagsRes, tagsRes] = await Promise.all([
        httpClient.get(
          `${process.env.REACT_APP_SERVER_URL}/api/RouteTags?Filter={"TenantId": ${tenantId}}&PageSize=100`
        ),
        httpClient.get(
          `${process.env.REACT_APP_SERVER_URL}/api/tags?Filter={"TenantId": ${tenantId}}&PageSize=100`
        ),
      ]);

      const routeTags = routeTagsRes.data;
      const tags = tagsRes.data;

      const mappedTags = tags.map((tagItem: any) => ({
        ...tagItem,
        routes: _.filter(routeTags, (o) => o.tagId === tagItem.id),
      }));

      // Apply custom order only in production
      let finalTags = mappedTags.reverse(); // keep your current reverse

      if (process.env.NODE_ENV === 'production') {
        const customOrder = [45, 46, 48, 44, 47]; // requested by client
        finalTags = [...finalTags].sort((a, b) => {
          const indexA = customOrder.indexOf(a.id);
          const indexB = customOrder.indexOf(b.id);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.id - b.id;
        });
      }

      dispatch(Action.getRouteTagsSuccess(finalTags));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `getRouteTagsLogic()`. ', { error });
      throw error;
    } finally {
      done();
    }
  },
});

const setNtpLogic = createLogic({
  type: Types.SET_NTP,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action }: any, dispatch, done) {
    const { data } = action.payload;

    dispatch(Action.setNtpSuccess(data));

    done();
  },
});

export default [updateSegmentsLogic, getRouteTagsLogic, getAllEtaLogic, setNtpLogic];
